// Etsy kategori ozellikleri — saf mantik (panel + sunucu yayin kuyrugu ortak).
// Doldurma sirasi: 1. kural (urun verisinden kesin) 2. sablon (sabit degerler)
// 3. AI (kalanlar; panelde aiProps, sunucuda services/etsy-properties-auto).

import type { ProductCategory } from "../types";

export interface EtsyTaxProp {
  property_id: number;
  name: string;
  display_name?: string;
  scales?: Array<{ scale_id: number; display_name: string }>;
  is_multivalued?: boolean;
  max_values_allowed?: number | null;
  supports_variations?: boolean;
  possible_values?: Array<{ value_id: number; name: string }>;
}

/** Taslaga yazilan ozellik (sunucu ListingPropertyInput ile ayni sekil) */
export interface ListingProp {
  propertyId: number;
  name: string;
  valueIds: number[];
  values: string[];
  scaleId?: number | null;
  source?: "rule" | "template" | "ai" | "manual";
}

/** Etsy'nin serbest varyasyon yuvalari — ozellik olarak doldurulmaz */
export const CUSTOM_PROPERTY_IDS = [513, 514, 516];

/** Takida sablon bos birakildiginda kullanilan tek degerler */
export const JEWELRY_PROPERTY_DEFAULTS: Record<string, string[]> = {
  Recipient: ["Women"],
  "Number of necklace strands": ["1"],
  Spinner: ["No"],
  "Jewelry closure type": ["Lobster claw"],
};

const propLabel = (p: EtsyTaxProp) => p.display_name || p.name;
export const isEnumerated = (p: EtsyTaxProp) => (p.possible_values?.length ?? 0) > 0;
export const maxValues = (p: EtsyTaxProp) => (p.is_multivalued ? Math.min(p.max_values_allowed || 5, 5) : 1);

/** Degerleri Etsy'nin izin verdikleriyle esler; eslesmeyen deger atilir.
 *  Sayisal ozellikte (Width) deger oldugu gibi, birim adiyla scale_id secilir. */
export function makeProp(tax: EtsyTaxProp, values: string[], source: ListingProp["source"], scaleName?: string): ListingProp | null {
  if (isEnumerated(tax)) {
    const picked = values
      .map((v) => tax.possible_values!.find((pv) => pv.name.toLowerCase() === v.trim().toLowerCase()))
      .filter((x): x is { value_id: number; name: string } => !!x);
    const uniq = [...new Map(picked.map((x) => [x.value_id, x])).values()].slice(0, maxValues(tax));
    if (!uniq.length) return null;
    return { propertyId: tax.property_id, name: propLabel(tax), valueIds: uniq.map((x) => x.value_id), values: uniq.map((x) => x.name), source };
  }
  const v = values.map((x) => x.trim()).filter(Boolean).slice(0, 1);
  if (!v.length) return null;
  const scale = tax.scales?.find((s) => s.display_name.toLowerCase() === (scaleName ?? "").toLowerCase()) ?? tax.scales?.[0];
  return { propertyId: tax.property_id, name: propLabel(tax), valueIds: [], values: v, scaleId: scale?.scale_id ?? null, source };
}

/** Ada gore ozellik: birebir ad/gorunen ad; yoksa ek almis ad ("Material multi"
 *  -> "Material", gorunen "Materials") */
export const byName = (taxProps: EtsyTaxProp[], name: string) => {
  const n = name.toLowerCase();
  return taxProps.find((p) => p.name.toLowerCase() === n || p.display_name?.toLowerCase() === n)
    ?? taxProps.find((p) => p.name.toLowerCase().startsWith(`${n} `) || p.display_name?.toLowerCase() === `${n}s`);
};

// ── Sablon varsayilanlari ────────────────────────────────────────────
// Sablon attributes'unda "Ad::Deger" dizisi olarak durur (kategoriden bagimsiz:
// ayni ad farkli taksonomilerde de eslesir).
export const TEMPLATE_DEFAULTS_KEY = "etsyPropertyDefaults";

export function parseTemplateDefaults(raw: unknown): Record<string, string[]> {
  const out: Record<string, string[]> = {};
  for (const x of Array.isArray(raw) ? raw : []) {
    const [name, value] = String(x).split("::");
    if (name && value) (out[name] ??= []).push(value);
  }
  return out;
}
export function serializeTemplateDefaults(map: Record<string, string[]>): string[] {
  return Object.entries(map).flatMap(([name, values]) => values.map((v) => `${name}::${v}`));
}

export function templateProps(taxProps: EtsyTaxProp[], defaults: Record<string, string[]>): ListingProp[] {
  return Object.entries(defaults)
    .map(([name, values]) => { const t = byName(taxProps, name); return t ? makeProp(t, values, "template") : null; })
    .filter((x): x is ListingProp => !!x);
}

// ── Kurallar: urun verisinden kesin cikanlar ─────────────────────────
export interface RuleContext {
  product: ProductCategory | null | undefined;
  materials?: string[];
  hasPersonalization?: boolean;
  variations?: Array<{ name: string; options: Array<{ label: string }> }>;
  colors?: string[];
}

export function ruleProps(taxProps: EtsyTaxProp[], ctx: RuleContext): ListingProp[] {
  const src = (ctx.product as unknown as { source?: { attributes?: Record<string, string>; descriptionText?: string } } | null)?.source;
  const attrs = src?.attributes ?? {};
  const text = [ctx.product?.name, ...Object.values(attrs), src?.descriptionText, ...(ctx.materials ?? [])]
    .filter(Boolean).join(" \n ").toLowerCase();
  const out: ListingProp[] = [];
  const add = (name: string, values: string[], scale?: string) => {
    const t = byName(taxProps, name);
    const p = t ? makeProp(t, values, "rule", scale) : null;
    if (p) out.push(p);
  };

  // Malzeme: yalnizca metinde acikca gecenler
  const mats: string[] = [];
  if (/925|sterling|gümüş|gumus|\bsilver\b/.test(text)) mats.push("Sterling silver");
  if (/stainless|çelik|celik/.test(text)) mats.push("Stainless steel");
  if (/pirinç|pirinc|\bbrass\b/.test(text)) mats.push("Brass");
  if (/titanium|titanyum/.test(text)) mats.push("Titanium");
  if (/\bcopper\b|bakır|bakir/.test(text)) mats.push("Copper");
  if (mats.length) add("Material", mats);

  // Altin: kaplama / vermeil / dolgu ve ayar
  const plated = /altın kaplama|altin kaplama|gold plat|gold-plat/.test(text);
  const solidity = /vermeil/.test(text) ? "Gold vermeil" : /gold filled|gold-filled/.test(text) ? "Gold filled" : plated ? "Gold plated" : null;
  if (solidity) {
    add("Gold solidity", [solidity]);
    const karat = text.match(/\b(9|10|14|18|20|22|24)\s*(k|kt|karat|ayar)\b/);
    if (karat) add("Gold purity", [`${karat[1]}k`]);
  }

  // Zincir tipi: ozellik degerlerinden metinde gecen (force = cable)
  const chainText = (Object.entries(attrs).find(([k]) => /zincir|chain/i.test(k))?.[1] ?? "").toLowerCase().replace(/\bforce\b/, "cable");
  const chainTax = byName(taxProps, "Chain style");
  if (chainText && chainTax) {
    const hit = chainTax.possible_values?.find((v) => chainText.includes(v.name.toLowerCase()));
    if (hit) add("Chain style", [hit.name]);
  }

  // Olcu: "13 × 22 mm" -> Width 13, Height 22 (mm)
  const size = text.match(/(\d+(?:[.,]\d+)?)\s*(?:mm)?\s*[x×]\s*(\d+(?:[.,]\d+)?)\s*mm/);
  if (size) {
    add("Width", [size[1].replace(",", ".")], "Millimeters");
    add("Height", [size[2].replace(",", ".")], "Millimeters");
  }

  // Ana renk: renk/kaplama ekseninin ilk secenegi, yoksa urun renkleri
  const colorAxis = ctx.variations?.find((v) => /colou?r|renk|finish|metal|tone/i.test(v.name));
  const colorCandidates = [...(colorAxis?.options.map((o) => o.label) ?? []), ...(ctx.colors ?? [])];
  const colorTax = byName(taxProps, "Primary color");
  const color = colorTax && colorCandidates
    .map((c) => colorTax.possible_values?.find((v) => v.name.toLowerCase() === c.toLowerCase().trim()))
    .find(Boolean);
  if (color) add("Primary color", [color.name]);

  if (/extender|uzatma|ayarlanabilir|adjustable/.test(text)) add("Adjustable", ["Yes"]);
  add("Can be personalized", [ctx.hasPersonalization ? "Yes" : "No"]);
  return out;
}

/** Katmanlari birlestirir: ONCE gelen kazanir (propertyId bazinda) */
export function mergeProps(...layers: Array<ListingProp[] | null | undefined>): ListingProp[] {
  const map = new Map<number, ListingProp>();
  for (const layer of layers) for (const p of layer ?? []) if (!map.has(p.propertyId) && p.values.length) map.set(p.propertyId, p);
  return [...map.values()];
}

/** Kural + sablon (AI'siz, aninda) */
export function baseProps(taxProps: EtsyTaxProp[], ctx: RuleContext, templateDefaults: Record<string, string[]>): ListingProp[] {
  return mergeProps(ruleProps(taxProps, ctx), templateProps(taxProps, templateDefaults));
}

/** Sablon varsayilanlari: sablonda tanimliysa o, degilse (takida) sabit liste */
export function effectiveTemplateDefaults(templateAttributes: unknown, isJewelry: boolean): Record<string, string[]> {
  const raw = (templateAttributes as Record<string, unknown> | null)?.[TEMPLATE_DEFAULTS_KEY];
  const parsed = parseTemplateDefaults(raw);
  return Object.keys(parsed).length ? parsed : isJewelry ? JEWELRY_PROPERTY_DEFAULTS : {};
}

